import React, { useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";

/** Dark text on a light course colour (Jebel Ali's yellow), white on the rest. */
const textOn = (hex) => {
  const m = String(hex || "").match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return undefined;
  const [r, g, b] = m.slice(1).map((part) => parseInt(part, 16));
  return (r * 299 + g * 587 + b * 114) / 1000 > 170 ? "#111827" : "#fff";
};

/**
 * What our DB holds for a race day: "missing" (ERA races, none here), "done" (every result in),
 * "partial" (some results missing), "card" (upcoming, races added) or null for nothing to say.
 */
export const dayStatus = (raceDay) => {
  if (!raceDay || raceDay.trial || !raceDay.races) return null;
  const past = !dayjs(raceDay.date).isAfter(dayjs(), "day");
  if (!raceDay.races.length) return past ? "missing" : null;
  if (!past) return "card";
  return raceDay.withResult.length === raceDay.races.length ? "done" : "partial";
};

const badgeText = (status, raceDay) =>
  status === "missing" ? "!" : status === "done" ? "✓" : status === "partial" ? `${raceDay.withResult.length}/${raceDay.races.length}` : raceDay.races.length;

/**
 * The admin's calendar (antd DatePicker), holding its date as "YYYY-MM-DD".
 * raceDays (date -> { course, color, trial }) colours the race days; legend is [[course, color]].
 * The popup stays inside this holder, so its styling cannot reach the rest of the admin.
 */
const DateField = ({ value, onChange, disableFuture, raceDays, legend }) => {
  const [open, setOpen] = useState(false);
  return (
  <div className="imp-cal-holder">
    <DatePicker
      format="DD MMM YYYY"
      placeholder="Select date"
      inputReadOnly
      open={open}
      onOpenChange={setOpen}
      value={value ? dayjs(value) : null}
      onChange={(picked) => {
        onChange(picked ? picked.format("YYYY-MM-DD") : "");
        setOpen(false);
      }}
      disabledDate={disableFuture ? (day) => day.isAfter(dayjs(), "day") : undefined}
      getPopupContainer={(node) => node.parentNode}
      renderExtraFooter={
        legend
          ? () => (
              <div className="imp-cal-legends">
                <div className="imp-cal-legend">
                  <strong>ERA</strong>
                  {legend.map(([course, color]) => (
                    <span key={course}><i style={{ backgroundColor: color }} />{course}</span>
                  ))}
                  <span><i className="imp-cal-legend-trial" />Trial</span>
                </div>
                <div className="imp-cal-legend">
                  <strong>Saddle Battle</strong>
                  <span><b className="imp-cal-badge imp-cal-badge--done">✓</b>All results</span>
                  <span><b className="imp-cal-badge imp-cal-badge--partial">5/8</b>Results added / races</span>
                  <span><b className="imp-cal-badge imp-cal-badge--missing">!</b>No races</span>
                  <span><b className="imp-cal-badge imp-cal-badge--card">8</b>Race card added</span>
                </div>
              </div>
            )
          : undefined
      }
      cellRender={
        raceDays
          ? (day, info) => {
              if (info.type !== "date") return info.originNode;
              const raceDay = raceDays.get(day.format("YYYY-MM-DD"));
              if (!raceDay) return info.originNode;
              const status = dayStatus(raceDay);
              return (
                <div
                  className={`ant-picker-cell-inner imp-cal-race ${raceDay.trial ? "imp-cal-trial" : ""}`}
                  style={raceDay.trial ? undefined : { backgroundColor: raceDay.color || undefined, color: textOn(raceDay.color) }}
                  title={`${raceDay.course}${raceDay.trial ? " (trial)" : ""}${raceDay.races ? ` · ${raceDay.races.length} races, ${raceDay.withResult.length} results` : ""}`}
                >
                  {day.date()}
                  {status && <span className={`imp-cal-badge imp-cal-badge--${status}`}>{badgeText(status, raceDay)}</span>}
                </div>
              );
            }
          : undefined
      }
    />
  </div>
  );
};

export default DateField;
